import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Mail, MapPin, Clock } from "lucide-react";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Select = ({ label, name, value, onChange, options, testid, placeholder }) => (
  <div>
    <label className="block text-xs font-medium text-steel mb-2 uppercase tracking-wider">{label}</label>
    <select name={name} value={value} onChange={onChange} className="field appearance-none" data-testid={testid}>
      <option value="" className="bg-carbon">{placeholder}</option>
      {options.map((o) => <option key={o} value={o} className="bg-carbon">{o}</option>)}
    </select>
  </div>
);

export default function LeadForm() {
  const { t } = useLang();
  const [form, setForm] = useState({ nome: "", azienda: "", email: "", ruolo: "", settore: "", consumo_annuo: "", temperatura_processo: "", tipo_richiesta: t.form.options.requestType[0], messaggio: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!t.form.options.requestType.includes(form.tipo_richiesta)) {
      setForm((f) => ({ ...f, tipo_richiesta: t.form.options.requestType[0] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success(t.form.toastOk);
      setForm({ nome: "", azienda: "", email: "", ruolo: "", settore: "", consumo_annuo: "", temperatura_processo: "", tipo_richiesta: t.form.options.requestType[0], messaggio: "" });
    } catch {
      toast.error(t.form.toastErr);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contatti" className="py-24 sm:py-32" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-5 gap-14">
        <div className="lg:col-span-2">
          <motion.p initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="overline-tag" data-testid="contact-overline">
            {t.form.overline}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
            className="mt-4 font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl"
          >
            {t.form.h2}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
            className="mt-5 text-steel leading-relaxed"
          >
            {t.form.intro}
          </motion.p>
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.3 }} className="mt-10 space-y-4 text-sm">
            <p className="flex items-center gap-3 text-steel"><Mail size={16} className="text-volt" /> info@vpsgroupsrl.com</p>
            <p className="flex items-center gap-3 text-steel"><MapPin size={16} className="text-volt" /> {t.form.location}</p>
            <p className="flex items-center gap-3 text-steel"><Clock size={16} className="text-volt" /> {t.form.reply}</p>
          </motion.div>
        </div>

        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="lg:col-span-3 glass rounded-2xl p-7 sm:p-10 space-y-6" data-testid="lead-form"
        >
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-medium text-steel mb-2 uppercase tracking-wider">{t.form.labels.name}</label>
              <input required name="nome" value={form.nome} onChange={onChange} className="field" placeholder={t.form.placeholders.name} data-testid="lead-nome-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-steel mb-2 uppercase tracking-wider">{t.form.labels.company}</label>
              <input required name="azienda" value={form.azienda} onChange={onChange} className="field" placeholder={t.form.placeholders.company} data-testid="lead-azienda-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-steel mb-2 uppercase tracking-wider">{t.form.labels.email}</label>
              <input required type="email" name="email" value={form.email} onChange={onChange} className="field" placeholder={t.form.placeholders.email} data-testid="lead-email-input" />
            </div>
            <Select label={t.form.labels.role} name="ruolo" value={form.ruolo} onChange={onChange} testid="lead-ruolo-select" placeholder={t.form.placeholders.select} options={t.form.options.role} />
            <Select label={t.form.labels.sector} name="settore" value={form.settore} onChange={onChange} testid="lead-settore-select" placeholder={t.form.placeholders.select} options={t.form.options.sector} />
            <Select label={t.form.labels.consumption} name="consumo_annuo" value={form.consumo_annuo} onChange={onChange} testid="lead-consumo-select" placeholder={t.form.placeholders.select} options={t.form.options.consumption} />
            <Select label={t.form.labels.temperature} name="temperatura_processo" value={form.temperatura_processo} onChange={onChange} testid="lead-temperatura-select" placeholder={t.form.placeholders.select} options={t.form.options.temperature} />
            <Select label={t.form.labels.requestType} name="tipo_richiesta" value={form.tipo_richiesta} onChange={onChange} testid="lead-tipo-select" placeholder={t.form.placeholders.select} options={t.form.options.requestType} />
          </div>
          <div>
            <label className="block text-xs font-medium text-steel mb-2 uppercase tracking-wider">{t.form.labels.message}</label>
            <textarea name="messaggio" value={form.messaggio} onChange={onChange} rows={4} className="field resize-none" placeholder={t.form.placeholders.message} data-testid="lead-messaggio-input" />
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full justify-center disabled:opacity-60 disabled:pointer-events-none" data-testid="lead-submit-button">
            {loading ? t.form.sending : t.form.submit} <ArrowRight size={16} />
          </button>
          <p className="text-xs text-steel/60">{t.form.privacy}</p>
        </motion.form>
      </div>
    </section>
  );
}
